import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { MeetListItem } from "../api/types";
import { accentFor } from "./MeetGraphic";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * Month-at-a-glance calendar that plots the user's meets on their day. Each day
 * cell shows up to a couple of meet pills (color-coded by format); extra meets
 * collapse into a "+N". Clicking a meet opens it; the header navigates months.
 */
export function MonthCalendar({ meets }: { meets: MeetListItem[] }) {
  const navigate = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );

  // Group meets by local YYYY-MM-DD for quick per-day lookup.
  const byDay = useMemo(() => {
    const map = new Map<string, MeetListItem[]>();
    for (const m of meets) {
      const key = ymd(new Date(m.startAt));
      const arr = map.get(key) ?? [];
      arr.push(m);
      map.set(key, arr);
    }
    for (const arr of map.values())
      arr.sort((a, b) => a.startAt.localeCompare(b.startAt));
    return map;
  }, [meets]);

  // Build the 6x7 grid of dates covering the visible month.
  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay()); // back to Sunday
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  const inMonth = (d: Date) => d.getMonth() === cursor.getMonth();
  const isToday = (d: Date) => ymd(d) === ymd(today);

  const go = (delta: number) =>
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  return (
    <div className="card cal">
      <div className="cal-head row spread">
        <button className="secondary" onClick={() => go(-1)} aria-label="Previous month">
          ‹
        </button>
        <strong className="cal-title">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </strong>
        <div className="row" style={{ gap: "0.4rem" }}>
          <button
            className="secondary"
            onClick={() =>
              setCursor(new Date(today.getFullYear(), today.getMonth(), 1))
            }
          >
            Today
          </button>
          <button className="secondary" onClick={() => go(1)} aria-label="Next month">
            ›
          </button>
        </div>
      </div>

      <div className="cal-grid cal-dow">
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className={"cal-dow-cell" + (i === 0 || i >= 5 ? " cal-weekend" : "")}
          >
            {w}
          </div>
        ))}
      </div>

      <div className="cal-grid">
        {cells.map((d) => {
          const key = ymd(d);
          const dayMeets = byDay.get(key) ?? [];
          const dow = d.getDay();
          const isWeekend = dow === 0 || dow === 5 || dow === 6;
          return (
            <div
              key={key}
              className={
                "cal-cell" +
                (isWeekend ? " cal-weekend" : " cal-weekday") +
                (inMonth(d) ? "" : " cal-cell-dim") +
                (isToday(d) ? " cal-cell-today" : "")
              }
            >
              <div className="cal-date">{d.getDate()}</div>
              <div className="cal-pills">
                {dayMeets.slice(0, isWeekend ? 3 : 1).map((m) => (
                  <button
                    key={m.meetId}
                    className={
                      "cal-pill" +
                      (m.status === "cancelled" ? " cal-pill-cancelled" : "")
                    }
                    style={{
                      ["--pill" as string]: accentFor(
                        (m as { format?: string }).format,
                      ),
                    }}
                    title={`${m.title}${m.status === "cancelled" ? " (cancelled)" : ""} · ${new Date(m.startAt).toLocaleTimeString(
                      [],
                      { hour: "numeric", minute: "2-digit" },
                    )}`}
                    onClick={() => navigate(`/meets/${m.meetId}`)}
                  >
                    {m.title}
                  </button>
                ))}
                {dayMeets.length > (isWeekend ? 3 : 1) && (
                  <span className="cal-more">
                    +{dayMeets.length - (isWeekend ? 3 : 1)} more
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
